// src/oura/liveAudit.ts
//
// Adapts a real GET /api/v1/businesses/{id} payload (ourteam-backend#3747,
// #3681-3684) into the exact shapes ../audit.ts's mock generators
// (getAudit/getAiVisibility) and ../connectors.ts's getConnectors already
// produce — BusinessAudit, AiVisibility, DataConnector[]. Those shapes are
// what BusinessOverview.tsx / SiteAudit.tsx / Questions.tsx / DataConnectors.tsx
// actually render; this is the seam #3160 should have used from the start
// instead of inventing a new screen. The BE built its blocks to match these
// mock functions field-for-field (see the header comment in
// app/dependencies/aeo_visibility.py), so this is closer to a reshape than a
// redesign.
//
// Also builds a minimal, HONEST ScoreSnapshot/Business shell for a live
// business — only the fields these three screens actually read outside of
// getAudit/getAiVisibility (confirmed by grep: capturedAt, and BusinessOverview's
// action-drawer wiring). Everything the fixture-only Action/Tracker system
// needs (pillars, headline, questions, rivals detail, actions, feed, history)
// stays empty/neutral rather than fabricated — see the "not adapted here"
// notes below.

import type {
    AeoBusinessDetail,
    AeoConnector as LiveConnector,
    AeoProbe,
    AeoSearchPerformance,
    AeoTracker,
    AeoGrounding,
} from "@service/aeo";
import { isFailingPage } from "@service/aeo";
import type { Business, ScoreSnapshot } from "./contract";
import type { BusinessAudit, AiVisibility, AuditIssue, Recommendation, UnderperformingPage } from "./audit";
import type { ConnectorKey, DataConnector } from "./connectors";
import type { ReportMetrics } from "./screens/Report";
import type { BusinessTracker, TrackerTask, TrackerWeek } from "./tracker";

/** True once a live business has a completed, non-gated assessment with a
 *  businessAudit block — i.e. there's real data to adapt. */
export function hasLiveResults(detail: AeoBusinessDetail): boolean {
    const la = detail.latest_assessment;
    return !!la && !la.gated && !!la.results?.businessAudit;
}

/**
 * Wire severity → the FE's own three levels.
 *
 * The BE sends `error` / `warning` / `notice` verbatim, straight from the
 * static CHECK_CATALOG in `app/dependencies/aeo_audit.py` — only `broken_4xx`
 * is an error, `canonicals` and `missing_alt` are notices, the rest are
 * warnings. None of those three values used to be KEYS here, so every check
 * fell through the `?? "warning"` default: the Errors tile was structurally 0
 * and a broken page looked the same as a missing alt attribute.
 *
 * The impact/effort words are kept because other producers still use them —
 * dropping them would silently re-introduce the same fallthrough for those.
 */
const SEVERITY_MAP: Record<string, AuditIssue["severity"]> = {
    error: "error",
    warning: "warning",
    notice: "notice",
    high: "error",
    critical: "error",
    medium: "warning",
    low: "notice",
    easy: "notice",
    hard: "error",
};

/**
 * BusinessAudit for BusinessOverview.tsx / SiteAudit.tsx. Only fields the BE
 * actually sends are filled; everything requiring per-page detail the BE
 * doesn't return yet (priorityPages/underperforming/traffic/pageSpeed/naming/
 * issue.pages[]) is an honest empty array/zeroed block, NOT fabricated —
 * those screens already render an empty state for zero-length lists.
 */
/** Scheme, `www.` and trailing slash removed — the same normalisation the
 *  Pages table joins on, and the BE's own `join_key`. */
/**
 * When the detail payload's assessment was captured (#3510).
 *
 * `captured_at` is a LIST-only field: `_score_point` synthesises it as
 * `_iso(completed_at or created_at)`. The detail endpoint serialises the
 * assessment row directly and sends those two instead, so every read of
 * `captured_at` here was `undefined` at runtime while typed as `string` —
 * which is how the header came to say "Never scanned" for a business scanned
 * the day before. Apply the BE's own rule instead of inventing another.
 */
function assessmentCapturedAt(la: {
    captured_at?: string;
    completed_at?: string;
    created_at?: string;
} | null | undefined): string {
    return la?.captured_at ?? la?.completed_at ?? la?.created_at ?? "";
}

function titleKey(url: string): string {
    return (url || "")
        .trim()
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "")
        .replace(/\/+$/, "")
        .toLowerCase();
}

export function adaptBusinessAudit(detail: AeoBusinessDetail): BusinessAudit | null {
    const la = detail.latest_assessment;
    const audit = la?.results?.businessAudit;
    if (!la || !audit) return null;

    const checks = la.results?.audit?.checks ?? [];
    // Every page's real <title>, from the crawl inventory, by normalised URL.
    const crawlTitles = new Map<string, string>();
    for (const p of la.results?.audit?.crawledPages ?? []) {
        const url = typeof p?.url === "string" ? p.url.trim() : "";
        const title = typeof p?.title === "string" ? p.title.trim() : "";
        if (url && title) crawlTitles.set(titleKey(url), title);
    }
    const issues: AuditIssue[] = checks.map((c) => ({
        key: c.key,
        label: c.label,
        severity: SEVERITY_MAP[c.severity] ?? "warning",
        count: c.count,
        desc: c.desc,
        tracker: "",
        about: c.about || c.desc,
        fix: c.fix ?? [],
        pageColLabel: c.pageColLabel ?? "",
        // What a draft can fix HERE, straight from the check (#4529). Passed
        // through including its absence: `[]` means the drawer offers no draft
        // at all, `undefined` means an older payload and the consumer falls
        // back to its own map. Collapsing the two would either hide a working
        // control or offer a meaningless one.
        draftTypes: c.draftTypes,
        locates: c.locates === true,
        // The BE DOES send affected pages on every check — an earlier version of
        // this adapter dropped them, which is why the issues table showed a count
        // with nothing behind it when expanded.
        //
        // Filtered to the pages that STILL have the problem. Once a business is
        // re-scanned, `pages[]` deliberately keeps the ones that were fixed
        // (#4350: that is how "fixed" becomes reportable instead of a row
        // silently vanishing between runs) and `count` narrows to mean only the
        // failing ones. Rendering the whole array under that count made the
        // header and the table state two different numbers for the same thing —
        // "3 pages" above a list of eight — and put pages the client had
        // already fixed back on their worklist. A first-run business has no
        // prior to carry, so every entry is `new` and nothing changes there,
        // which is exactly why this only ever showed up on re-scans.
        pages: (c.pages ?? []).filter(isFailingPage).map((p) => ({
            url: p.url,
            // The crawl inventory FIRST, this check's own copy second.
            //
            // Each check carries its own title and they do not all agree: the
            // Core Web Vitals check hard-codes "Homepage" for the homepage
            // entry (the PSI block in aeo_audit.py) while every other check
            // goes through `_page_entry` and reports the crawled <title>. So
            // one page read "Homepage" in that drawer and "Blanc Dental Clinic
            // | Perfecting Your Smile" in the table beside it. Resolving from
            // the inventory makes every surface name a page the same way,
            // whichever check put it on screen.
            title: crawlTitles.get(titleKey(p.url)) || p.title || p.url,
            discovered: p.discovered || assessmentCapturedAt(la),
            meta: p.meta ?? "",
            // What a person asserted about this page, beside what the crawl
            // measured (#4530). Never merged into `status` above: the whole
            // point is that the two are kept apart and compared.
            operatorStatus: p.operatorStatus ?? null,
            operatorStatusAt: p.operatorStatusAt ?? null,
            operatorStatusBy: p.operatorStatusBy ?? null,
            statusDisagreement: p.statusDisagreement ?? null,
            // Where the problem actually is, for the checks no draft can fix.
            locations: p.locations ?? [],
            deadLinks: p.deadLinks ?? [],
        })),
    }));
    const errors = issues.filter((i) => i.severity === "error").reduce((n, i) => n + i.count, 0);
    const warnings = issues.filter((i) => i.severity === "warning").reduce((n, i) => n + i.count, 0);
    // The crawl did not run, or read nothing (`run_site_audit`'s stub, and the
    // "0 of N readable" case). The backend still sends a full healthByArea
    // then: 0% crawlability, 0% linking, markup missing, robots.txt not found.
    // Four failures nobody measured, so the block is dropped here instead.
    const crawlFailed = la.results?.audit?.mode === "visibility_only" || la.results?.audit?.pagesCrawled === 0;

    return {
        siteHealthScore: audit.siteHealthScore,
        // Passed through so the Pages card can tell "we could not read the
        // site" from "we read it and found nothing" (#3782).
        siteHealthScoreAbsent: audit.siteHealthScoreAbsent ?? null,
        siteHealthReadablePages: audit.siteHealthReadablePages ?? null,
        // The third gauge (#3765). Passed through INCLUDING its absence: a run
        // from before ourteam-backend#4877 carries no score and no reason, and
        // defaulting either one would invent a measurement.
        aiReadabilityScore: audit.aiReadabilityScore ?? null,
        aiReadabilityVerdict: audit.aiReadabilityVerdict ?? null,
        aiReadabilityAbsent: audit.aiReadabilityAbsent ?? null,
        aeoScoreAbsent: audit.aeoScoreAbsent ?? null,
        aiReadabilityComponents: audit.aiReadabilityComponents ?? [],
        // Absent on every stored scan: it exists only once a refresh has run
        // (ourteam-backend#4995), and absent means "measured with the scan".
        aiReadabilityMeasuredAt: audit.aiReadabilityMeasuredAt ?? null,
        // The counts behind a withheld score. Absent on older scans, and absent
        // is not zero — the card simply says less.
        aiReadabilityCrawl: audit.aiReadabilityCrawl ?? null,
        // Omitted on the wire reads as absent, never as 0 (#4096).
        aeoScore: audit.aeoScore ?? null,
        siteHealthVerdict: audit.siteHealthVerdict,
        aeoVerdict: audit.aeoVerdict,
        // One number on the wire, two fields here: the fixtures separate a
        // pillar score from the crawl-error percentage, the live BE does not.
        siteHealth: audit.siteHealthScore,
        // Lives on the raw audit block, not businessAudit.
        pagesCrawled: la.results?.audit?.pagesCrawled ?? 0,
        // The scan's own denominator — null (not 0) when an older run never
        // sent it, so consumers fall back to counting rows instead of
        // rendering "30 of 0 pages".
        pagesKnown: la.results?.audit?.pagesKnown ?? null,
        truncated: la.results?.audit?.truncated === true,
        // Carried, not inferred: the notice on the Pages card has to name which
        // limit stopped the crawl (#3529).
        truncationReason: la.results?.audit?.truncation?.reason,
        errors,
        warnings,
        issues,
        strengths: audit.strengths,
        weaknesses: audit.weaknesses,
        // These three are ALWAYS sent by build_business_audit(). They used to be
        // hard-coded empty here on the assumption they were still to come, which
        // meant a real assessment rendered its priority pages, underperforming
        // pages and recommendations as blank states — worse than missing, because
        // an empty list reads as "we looked and found none".
        priorityPages: audit.priorityPages ?? [],
        underperforming: (audit.underperforming ?? []).map((p) => ({
            ...p,
            // The BE emits only "404" | "Orphan page" | "Thin content"; the FE
            // union carries a fourth it never sends. Narrowed here rather than
            // widened there, so an unexpected value is visible instead of silent.
            reason: p.reason as UnderperformingPage["reason"],
        })),
        // Passed through as-is, INCLUDING its absence. The zero-filled fallback
        // that used to sit here destroyed the only signal that distinguishes
        // "no analytics connector" from "measured, and it really is zero" —
        // and the stat tiles then presented that default as a measurement, so
        // an unconnected business was told it had a 0% bounce rate.
        // Source is GA4 (`_ga4_pull`), not Search Console.
        //
        // Two unit fixes on the way through, both against what `_ga4_pull`
        // actually sends:
        //   • bounceRate arrives as GA4's fraction (0.452). Every consumer
        //     appends "%", so passing it through rendered "0.452%".
        //   • deltaPct is a hard-coded 0.0 until the BE computes a trend
        //     (ourteam-backend#4942). Rendered, that is a green "+0%" claiming a
        //     measured "no change". It rides with the trend: no trend, no delta.
        traffic: audit.traffic && {
            ...audit.traffic,
            bounceRate: Math.round(audit.traffic.bounceRate * 1000) / 10,
            deltaPct: audit.traffic.trend.length > 0 ? audit.traffic.deltaPct : null,
            window: audit.traffic.window ?? null,
            series: audit.traffic.series ?? null,
        },
        // Measured, no sessions (ourteam-backend#6749): only without `traffic`, and only with both dates.
        trafficEmpty:
            !audit.traffic && audit.trafficEmpty?.window?.start && audit.trafficEmpty.window.end
                ? { start: audit.trafficEmpty.window.start, end: audit.trafficEmpty.window.end, days: audit.trafficEmpty.window.days ?? 28 }
                : null,
        // Real per-area health from the same crawl. Previously ignored, while
        // the card computed its own numbers and hard-coded two of them.
        healthByArea: crawlFailed ? undefined : la.results?.audit?.healthByArea,
        crawlFailed,
        crawlFailedReason: la.results?.audit?.mode_reason ?? null,
        // The crawl's own date: a crawl refresh moves it without moving the scan.
        crawledAt: la.results?.audit?.generated_at ?? (assessmentCapturedAt(la) || null),
        recommendations: (audit.recommendations ?? []).map((r) => ({
            ...r,
            // The plan's opinion of this card (#4528). Spread already carries
            // these; named here so the join is visible at the seam where the
            // two surfaces used to disagree about the same finding.
            workState: r.workState ?? null,
            workStateLabel: r.workStateLabel ?? null,
            dim: r.dim === true,
            // Same narrowing as `reason` above: the wire types these as plain
            // strings because the BE builds them from a lookup with defaults.
            effort: r.effort as Recommendation["effort"],
            horizon: r.horizon as Recommendation["horizon"],
        })),
        competitors: audit.competitors.map((c) => ({
            name: c.name,
            domain: c.domain,
            aeo: c.aeo,
            seo: c.seo,
            queries: c.queries,
            note: c.note,
        })),
        naming: { capturedAt: assessmentCapturedAt(la), entries: [] },
        // Field names match 1:1 with PageSpeed/PageSpeedRun (see _parse_run() in
        // app/dependencies/aeo_pagespeed.py) — pass through when present; the BE
        // omits the block entirely when every PSI call failed, never fabricates.
        pageSpeed: (la.results?.pageSpeed as unknown as BusinessAudit["pageSpeed"]) ?? {
            capturedAt: assessmentCapturedAt(la),
            runs: [],
            pages: [],
        },
        // The crawl inventory (#3957). It has been arriving in this payload all
        // along — `to_status_dict()` ships `results` whole, with no allowlist —
        // it simply was not declared here, so nothing could read it.
        //
        // Left `undefined` when the run predates it, rather than defaulted to
        // []: consumers must be able to tell "this scan never reported an
        // inventory" from "the inventory is empty", and only the second one
        // means the crawl found nothing.
        crawledPages: la.results?.audit?.crawledPages?.map((p) => ({
            url: p.url,
            // Kept EMPTY when the crawler found no <title>, never filled with
            // the url. Empty here is the measurement two things depend on: the
            // "Missing page title" finding, and the Pages table's fallback for
            // names Search Console did not supply. A url standing in for a
            // title reads as a real title to both.
            title: p.title ?? "",
            // Defaulted to [] per PAGE, which is the honest read: a row exists
            // because the crawler fetched that page, and a missing `failed`
            // means it failed nothing.
            failed: p.failed ?? [],
            status: p.status,
            contentType: p.contentType,
            pageKind: p.pageKind,
        })),
    };
}

/**
 * AiVisibility for Questions.tsx. The BE's aiVisibility block (#3684) is
 * shaped almost identically to this type already — see
 * app/dependencies/aeo_visibility.py's own header comment, which names this
 * exact FE consumer. `null` when the run hasn't produced this block yet
 * (still gated, or engine stubbed with no probes).
 */
export function adaptAiVisibility(detail: AeoBusinessDetail): AiVisibility | null {
    const vis = detail.latest_assessment?.results?.aiVisibility;
    if (!vis) return null;
    // Field names already match 1:1 (score, verdict, blurb, capturedAt, mentions,
    // mentionsDeltaPct, citations, citationsDeltaPct, citedPages,
    // citedPagesDeltaPct, trend, engines[], countries[]) — pass through.
    // `engine` is typed as a wider `string` on the wire than the FE's closed
    // union ("ChatGPT" | "Gemini" | "Google AI"); the BE catalog is fixed to
    // exactly those three, so the cast is safe.
    return vis as unknown as AiVisibility;
}

/**
 * The only connectors we surface today. The BE catalog also carries Bing,
 * Webflow, Shopify and Squarespace, but none are wired to a connect flow —
 * listing an integration nobody can complete is a promise the product can't
 * keep, so they're filtered out here rather than shown as permanently
 * "Not connected".
 *
 * Framer landed in the BE catalog with ourteam-backend#3801 and is live.
 * `gbp` was DROPPED from that catalog in the same ticket — it could never reach
 * `connected` (the GBP API needs a separate Google application and approval), so
 * listing it promised an integration nobody could complete. It stays in this set
 * only so an older BE that still returns it degrades gracefully; remove it once
 * every environment is past #3801.
 *
 * `wix` joined on the commit that made it completable, not before:
 * ourteam-backend#4758 validates and stores the connection and #4762 put it in
 * `LIVE_CONNECTORS`, with ourteam-frontend#3691 supplying the form. The rule
 * this set exists for is "can a person finish this?", and for Wix the answer
 * changed — so the entry changed with it.
 *
 * **This set is a SECOND gate on top of the backend's `live` flag, and that is
 * the trap.** The BE decides what is offerable and already ships Wix dark until
 * it works; this list then decides what is rendered. Adding a connector to the
 * catalog, the `ConnectorKey` union, the label map and the logo switch is not
 * enough — a key missing from here is dropped by `adaptConnectors` with no
 * error anywhere, which is exactly how Wix shipped its whole FE and still did
 * not appear on the Connectors tab.
 *
 * `google_docs` is status only (documents phase 1): Oura reads the client's
 * Drive folder as oura@ourteam.ai, so there is nothing to finish. Its row is
 * drawn whether or not the catalog carries it, with its state read from the
 * documents service; listing it here keeps the backend's entry from being
 * dropped on the way.
 */
const VISIBLE_CONNECTORS: ReadonlySet<ConnectorKey> = new Set<ConnectorKey>(["gsc", "ga4", "wordpress", "gbp", "framer", "wix", "google_docs"]);

/**
 * How the visibility number was measured (#3564).
 *
 * Straight off `results.grounding`, with no defaulting: a scan from before the
 * field existed returns null, and the surfaces then say nothing about the
 * instrument — which is exactly what they did before. Inventing a label would
 * claim a measurement nobody made.
 */
export function adaptGrounding(detail: AeoBusinessDetail): AeoGrounding | null {
    return detail.latest_assessment?.results?.grounding ?? null;
}

/**
 * The point in the trend where the question set was re-authored (#3539).
 *
 * `null` when this run did not re-author, or when the business had no prior
 * scan — the BE only stamps `queries_trend_break` when both are true, because
 * that is the only case where the trend line joins two points measured with
 * different rulers.
 *
 * Deliberately NOT derived from anything else. `prompt_version` would be the
 * more general signal ("these two points used different question sets, whoever
 * changed them"), but it is a column on the assessment that
 * `to_status_dict()` never emits, so the FE cannot see it — handed to the BE
 * rather than guessed at from the data we do have.
 */
export function adaptTrendBreak(
    detail: AeoBusinessDetail,
): { at: string; reason: string } | null {
    const brk = detail.latest_assessment?.results?.queries_trend_break;
    if (!brk?.at) return null;
    return { at: brk.at, reason: brk.reason || "the question set changed here" };
}

/** DataConnector[] for DataConnectors.tsx. Field names already match the real
 *  catalog exactly (key/label/category/blurb/status/detail/lastSync) — the
 *  cast is safe because the BE's connector keys are the same fixed catalog
 *  the FE union types, just not statically shared across the service/feature
 *  boundary (services/aeo doesn't import from oura/*). */
export function adaptConnectors(raw: LiveConnector[]): DataConnector[] {
    // The old `as unknown as DataConnector[]` asserted the whole array at once,
    // which silently asserted `key` too — so a BE key the FE union had never
    // heard of type-checked fine and rendered a blank logo tile. That is how
    // `framer` shipped faceless. The gate is now a type predicate: only a key
    // this file actually knows about survives the filter, and the compiler —
    // not a cast — is what says so.
    return raw
        .filter((c): c is LiveConnector & { key: ConnectorKey } =>
            VISIBLE_CONNECTORS.has(c.key as ConnectorKey))
        .map((c) => ({ ...c, key: c.key }) as DataConnector);
}

/**
 * Minimal, honest ScoreSnapshot shell for a live business — NOT a fixture
 * substitute. Every field a screen actually reads is real; every field only
 * the fixture-only Action/Tracker/History systems need is neutral-empty, not
 * invented. See BusinessOverview.tsx/SiteAudit.tsx/Questions.tsx: outside of
 * getAudit/getAiVisibility they only read `snapshot.capturedAt` and (Overview
 * only) `snapshot.actions` for the fix-drawer mapping, which stays empty here
 * — the fix drawer is fixture-only and out of scope (see #3160).
 */
function competitorWinsFor(la: AeoBusinessDetail["latest_assessment"], name: string): string[] {
    const wins = la?.results?.aiVisibility?.competitorWins ?? [];
    const key = name.trim().toLowerCase();
    return wins.find((w) => w.name.trim().toLowerCase() === key)?.queries ?? [];
}

/**
 * Questions at least one engine named, out of the questions measured, straight
 * off `results.aiVisibility` (`questionsNamed` / `questionsOf`): the backend's
 * own count, the one the Visibility header prints (#4154).
 *
 * Both zero when the scan carries no count (a scan from before it existed, or
 * one nothing answered). A zero here means "not measured", never "0 of 0
 * named": the report hides the line on `questionsTested === 0`.
 */
function headlineCounts(la: AeoBusinessDetail["latest_assessment"]): { questionsTested: number; mentionsTotal: number; namedRate: number } {
    const vis = la?.results?.aiVisibility;
    const named = vis?.questionsNamed;
    const of = vis?.questionsOf;
    if (typeof named !== "number" || typeof of !== "number" || of <= 0) {
        return { questionsTested: 0, mentionsTotal: 0, namedRate: 0 };
    }
    const count = Math.min(Math.max(named, 0), of);
    return { questionsTested: of, mentionsTotal: count, namedRate: count / of };
}

export function buildLiveSnapshot(detail: AeoBusinessDetail): ScoreSnapshot {
    const la = detail.latest_assessment;
    const audit = la?.results?.businessAudit;
    return {
        id: la?.assessment_id ?? detail.id,
        businessId: detail.id,
        // NOT `new Date(0)`. That fallback turned "this run has no timestamp"
        // into a real-looking date, and every consumer rendered it: the traffic
        // range read "10 Oct to 1 Jan 1970" and the AI-answers line read
        // "checked 29 Dec to 4 Jan 1970". An empty string is falsy, so the
        // date helpers already degrade to "—" and the two headline consumers
        // below drop the clause entirely rather than print a placeholder.
        // `captured_at` exists only on the LIST payload, where the BE synthesises
        // it as `_iso(completed_at or created_at)` in `_score_point`. THIS is the
        // detail payload, whose `latest_assessment` ships `completed_at` and
        // `created_at` and no `captured_at` at all — so reading that name alone
        // yielded "" for every live business, and the header then stated
        // "Never scanned" for a business scanned the day before (#3510).
        // Apply the BE's own rule rather than inventing a different one.
        capturedAt: assessmentCapturedAt(la),
        gated: la?.gated ?? true,
        gateReasons: [], // BE returns a bare boolean today, no reasons list (see #3160 investigation)
        // Still AEO + site health, unchanged. Search performance (#3818) is a
        // real third number now, but it is null for any business without Search
        // Console connected — folding it in would silently change what `total`
        // means from one business to the next, and break comparison against
        // every score captured before it existed.
        // Only when BOTH scores exist: an absent one must not read as 0 and
        // halve the blend (Jack, PR #3820).
        total: audit && typeof audit.aeoScore === "number" && typeof audit.siteHealthScore === "number"
            ? Math.round((audit.aeoScore + audit.siteHealthScore) / 2)
            : null,
        level: null,
        levelName: "",
        plusMinus: 0,
        samplesPerCell: 0,
        previousTotal: null,
        pillars: [], // no 5-pillar breakdown from the BE yet (#3160 Phase 2)
        headline: {
            ...headlineCounts(la),
            linkRate: 0,
            bestGooglePosition: null,
            sentiment: null,
            sentimentSuppressed: true,
            plainSummary: "",
        },
        questions: [], // getFiftyQuestions() pads an empty list to 50 generated ones — no fabrication in THIS shell
        // Which questions each rival won comes from the measured tally the scan
        // already computes (`aiVisibility.competitorWins`), never from the seeded
        // rival list. The ids ARE the question texts: the live snapshot carries
        // no question rows, and every consumer falls back to the id as the text.
        rivals: (audit?.competitors ?? []).map((c) => ({
            name: c.name,
            domain: c.domain,
            winsQuestionIds: competitorWinsFor(la, c.name),
            whatTheyHave: c.note,
            reviewCount: null,
            reviewRating: null,
            total: c.aeo,
            previousTotal: null,
        })),
        setup: { site: [], sources: [], reputation: [], plainSummary: "" },
        connections: [],
        actions: [], // fixture-only Action/fix-drawer system — out of scope, see module header
        feed: [], // no BE activity-feed source exists (#3160 investigation)
        history: [], // no monthly rollup endpoint yet — History tab renders its own empty state
    };
}

export function buildLiveBusiness(detail: AeoBusinessDetail): Business {
    return {
        id: detail.id,
        name: detail.business_name,
        domain: detail.domain,
        vertical: detail.category,
        city: detail.city,
        state: detail.state,
        plan: detail.plan,
        // Passed straight through, null-safe: the merchant workspace only exists
        // once staff have created the merchant's login.
        companyId: detail.company_id ?? null,
        basePath: detail.base_path ?? "",
        faviconUrl: detail.favicon_url ?? null,
    };
}

/**
 * `results.searchPerformance` (ourteam-backend#3818) — real Search Console
 * numbers. Passed through rather than adapted: the BE block was designed
 * against this consumer, so the field names already match.
 *
 * `undefined` on a fixture business, and on any run finalized before #3818
 * shipped. That is DIFFERENT from `{connected: false}`, which means the run
 * measured and found no Search Console connection — the screen renders the same
 * connect prompt for both, but only because there is nothing more specific we
 * can honestly say about the older runs.
 */
export function adaptSearchPerformance(detail: AeoBusinessDetail): AeoSearchPerformance | undefined {
    return detail.latest_assessment?.results?.searchPerformance;
}

/**
 * `results.probes[]` (ourteam-backend#3803) — one engine answer per question.
 *
 * **Absent, not empty**, when the engine never ran, so the caller can tell "not
 * measured" from "measured, found nothing". Returning [] here would collapse
 * that distinction and the per-question grid would render an empty table as
 * though every question came back with no mention.
 */
export function adaptProbes(detail: AeoBusinessDetail): AeoProbe[] | undefined {
    return detail.latest_assessment?.results?.probes;
}

/**
 * The report metrics block (ourteam-backend#3802) — area scores, maturity,
 * AI-readability, sentiment and answer coverage.
 *
 * These live at the top level of `results`, not inside a wrapper, so this
 * gathers them into the shape the Report component takes. Returns `undefined`
 * when NONE of them are present (a fixture business, or a run finalized before
 * #3802), so the report renders without the sections rather than with a row of
 * "not measured" placeholders.
 */
/**
 * What the Share report page may print as a headline for a live scan, and
 * nothing it has to invent (#4238). `buildLiveSnapshot` is a shell with
 * placeholders (`level: null`, `plusMinus: 0`, no best Google position), so the
 * page reads these instead: the scan's own AEO score, and whether Search
 * Console is connected, which decides if "not measured" is the honest word for
 * the Google line. Both `null` when the scan does not say.
 */
export interface LiveHeadline {
    aeoScore: number | null;
    searchConnected: boolean | null;
}

export function liveHeadline(detail: AeoBusinessDetail): LiveHeadline {
    const r = detail.latest_assessment?.results;
    const score = r?.businessAudit?.aeoScore;
    const connected = r?.searchPerformance?.connected;
    return {
        aeoScore: typeof score === "number" ? score : null,
        searchConnected: typeof connected === "boolean" ? connected : null,
    };
}

export function adaptReportMetrics(detail: AeoBusinessDetail): ReportMetrics | undefined {
    const r = detail.latest_assessment?.results;
    if (!r) return undefined;
    const has = r.areaScores !== undefined
        || r.aiReadability !== undefined
        || r.sentiment !== undefined
        || r.answerCoverage !== undefined;
    if (!has) return undefined;
    // `reportMetrics` first (#3785). The top-level copy is spread at finalize as
    // `maturity_level` / `maturity_label`, so `r.maturityLevel` was always
    // undefined on a live run and "Overall: Level N" never rendered at all —
    // which is why nobody could see the level, let alone why it was capped.
    const rm = r.reportMetrics;
    return {
        areaScores: r.areaScores,
        maturityLevel: rm?.maturityLevel ?? r.maturityLevel,
        maturityLabel: rm?.maturityLabel ?? r.maturityLabel,
        levelCappedBy: rm?.levelCappedBy ?? null,
        aiReadability: r.aiReadability,
        sentiment: r.sentiment,
        answerCoverage: r.answerCoverage,
    };
}

/**
 * The BE tracker payload (ourteam-backend#3686) as the Tracker screen's own
 * BusinessTracker.
 *
 * `serialize_tracker()` was written against this exact shape, so this is a
 * narrowing rather than a translation: the only work is tightening the wire's
 * open `string` unions (status, owner, week) into the closed ones the screen
 * types. Done here, at the seam, rather than widening the screen — an
 * unexpected value from the BE should surface as a rendering oddity in one
 * place, not weaken every type downstream.
 */
export function adaptTracker(raw: AeoTracker): BusinessTracker {
    return {
        reference: raw.reference,
        packageName: raw.packageName,
        window: raw.window,
        // `??`, not `||`: Kickoff is week 0 (ourteam-backend#4476) and `0 || 1`
        // is 1, so a plan sitting in Kickoff reported itself as being in Week 1.
        // Only a MISSING value should fall back.
        currentWeek: (raw.currentWeek ?? 1) as BusinessTracker["currentWeek"],
        // The plan's own build stamp (#4497), distinct from the scan's capture
        // date. Passed through rather than defaulted: null means "this blob
        // predates the field", which is a different answer from a date.
        generatedAt: raw.generatedAt ?? null,
        generatedFrom: raw.generatedFrom ?? null,
        startDate: raw.startDate ?? null,
        startDateSource: raw.startDateSource ?? null,
        startDateNote: raw.startDateNote ?? null,
        templateVersion: raw.templateVersion,
        // Proposals, not tasks (#4513). Narrowed here like everything else at
        // this seam; absent on a plan built before the queue existed, which is
        // an empty queue rather than a missing feature.
        suggestions: (raw.suggestions ?? []).map((sg) => ({
            id: sg.id,
            rule: sg.rule,
            week: sg.week,
            action: sg.action,
            notes: sg.notes,
            why: sg.why,
            evidence: sg.evidence as { urls?: string[]; titles?: string[] } | undefined,
            state: sg.state,
        })),
        nextDeliverable: raw.nextDeliverable,
        scoreboard: raw.scoreboard,
        weeks: raw.weeks.map((w) => ({
            weeks: w.weeks ?? 1,
            label: w.label,
            week: w.week as TrackerWeek["week"],
            title: w.title,
            goal: w.goal,
            deliverable: w.deliverable,
            dates: w.dates,
            tasks: w.tasks.map((t) => ({
            targetDate: t.targetDate ?? null,
            targetDateSource: t.targetDateSource ?? null,
                id: t.id,
                action: t.action,
                notes: t.notes,
                tool: t.tool,
                gate: t.gate,
                owner: t.owner as TrackerTask["owner"],
                status: t.status as TrackerTask["status"],
                auto: t.auto,
                // #3817's traceability — carried through so the fix drawer can
                // say what this task exists to fix.
                source: t.source,
                sourceQueries: t.sourceQueries,
                targetPage: t.targetPage,
                cause: t.cause,
                closedBy: t.closedBy,
                touchpoint: t.touchpoint ?? null,
                // Dropped here until now, so a contradiction task could never
                // show its two quotes on live data. The BE does not serialize
                // them yet either; carried so the row lights up when it does.
                templateKey: t.templateKey,
                evidence: t.evidence,
                // Optional BE handoffs, absent on live today: the Fixes screen
                // falls back honestly when they are missing.
                helps: t.helps,
                doneAt: t.doneAt,
                doneBy: t.doneBy,
                impact: t.impact,
                // #4353's worklist. Carried, not re-derived: the BE joins the
                // task to its check's failing pages server-side and ships the
                // result. The FE used to redo that join from the audit block
                // and only inside the fix drawer, so the tracker ROW — the
                // thing people actually scan — could not say what it covered.
                //
                // Passed through INCLUDING its absence: an older payload sends
                // nothing, which is not the same claim as `[]`.
                affectedPages: t.affectedPages,
                affectedPageCount: t.affectedPageCount,
            })),
        })),
        fixRegister: raw.fixRegister.map((r) => ({
            issueKey: r.issueKey,
            issue: r.issue,
            detectedWhere: r.detectedWhere,
            fixedAt: r.fixedAt,
            status: r.status as BusinessTracker["fixRegister"][number]["status"],
            note: r.note,
        })),
    };
}
